/**
 * Crop arithmetic for any rectangular frame (FED-66, 12 Oct). David: "let's
 * add some crop ability for photos uploaded to feed."
 *
 * The card photo's square crop (profilePhoto.ts, 6 Oct) was the first user and
 * is now a square frame passed through these same functions, so there is one
 * home for the maths (standing rule 11). Pure functions: the suite proves them
 * without a canvas.
 *
 * ── THE MODEL ───────────────────────────────────────────────────────────────
 *
 *   frame  the box on screen, in CSS pixels. Its shape is the crop's shape.
 *   zoom   1 = the photo exactly COVERS the frame: nothing but photo is ever
 *          in it, so there is no letterbox to save. Up to 4x, less for a
 *          small photo (maxZoomIn).
 *   cx,cy  the point of the photo, in the photo's own pixels, at the frame's
 *          centre.
 *
 * Every move goes through clampCropIn, which keeps the frame inside the photo:
 * a drag past the edge stops at the edge instead of showing grey.
 *
 * ── WHY THE RESULT IS FRACTIONS, NOT PIXELS ─────────────────────────────────
 *
 * A feed photo exists twice: the 2,000 px feed copy and (when licensed, 146)
 * the full-size copy, both made from the picked file. A crop in pixels of one
 * is wrong for the other. The crop is kept as fractions of the upright photo
 * (NormRect) and each copy cuts its own pixels from it, so a tag placed on the
 * feed copy (161's x/y) lands on the same person in the full-size copy, and
 * whatever the poster cropped out stays out of a Gallery download.
 */

export type Frame = { w: number; h: number }
export type Crop = { zoom: number; cx: number; cy: number }
/** In photo pixels. */
export type CropBox = { sx: number; sy: number; sw: number; sh: number }
/** Fractions (0..1) of the upright photo. x,y is the top left corner. */
export type NormRect = { x: number; y: number; w: number; h: number }

export const WHOLE: NormRect = { x: 0, y: 0, w: 1, h: 1 }

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi)

/** Screen pixels per photo pixel at zoom 1: the scale at which the photo
 *  exactly covers the frame. */
export function coverScale(f: Frame, w: number, h: number): number {
  return Math.max(f.w / w, f.h / h)
}

/** A crop never goes below 128 photo pixels on its short side — past that
 *  it is a blur, and 4x is plenty for a face in a group shot. */
export function maxZoomIn(f: Frame, w: number, h: number): number {
  const shortAtOne = Math.min(f.w, f.h) / coverScale(f, w, h)
  return Math.max(1, Math.min(4, shortAtOne / 128))
}

export function scaleIn(f: Frame, w: number, h: number, zoom: number): number {
  return coverScale(f, w, h) * zoom
}

export function clampCropIn(f: Frame, w: number, h: number, c: Crop): Crop {
  const zoom = clamp(c.zoom, 1, maxZoomIn(f, w, h))
  const s = scaleIn(f, w, h, zoom)
  const hw = f.w / (2 * s)
  const hh = f.h / (2 * s)
  return { zoom, cx: clamp(c.cx, hw, w - hw), cy: clamp(c.cy, hh, h - hh) }
}

/** Zoom 1, centred. */
export function initialCropIn(w: number, h: number): Crop {
  return { zoom: 1, cx: w / 2, cy: h / 2 }
}

/** A drag of (dx, dy) SCREEN pixels moves the photo with the finger. */
export function panCropIn(f: Frame, w: number, h: number, c: Crop, dx: number, dy: number): Crop {
  const s = scaleIn(f, w, h, c.zoom)
  return clampCropIn(f, w, h, { ...c, cx: c.cx - dx / s, cy: c.cy - dy / s })
}

/**
 * Zoom to `zoom`, keeping the photo point under (ax, ay) — screen pixels from
 * the frame's centre — where it is, so a pinch zooms INTO what is between
 * your fingers rather than towards the middle.
 */
export function zoomCropIn(f: Frame, w: number, h: number, c: Crop, zoom: number, ax = 0, ay = 0): Crop {
  const z = clamp(zoom, 1, maxZoomIn(f, w, h))
  const s0 = scaleIn(f, w, h, c.zoom)
  const s1 = scaleIn(f, w, h, z)
  const px = c.cx + ax / s0
  const py = c.cy + ay / s0
  return clampCropIn(f, w, h, { zoom: z, cx: px - ax / s1, cy: py - ay / s1 })
}

/** The box, in photo pixels, that the frame is showing. */
export function cropBoxIn(f: Frame, w: number, h: number, c: Crop): CropBox {
  const s = scaleIn(f, w, h, c.zoom)
  const sw = f.w / s
  const sh = f.h / s
  return { sx: c.cx - sw / 2, sy: c.cy - sh / 2, sw, sh }
}

/** The frame's box as fractions of the photo, clamped to it. */
export function normRectOf(box: CropBox, w: number, h: number): NormRect {
  const x = clamp(box.sx / w, 0, 1)
  const y = clamp(box.sy / h, 0, 1)
  return { x, y, w: clamp(box.sw / w, 0, 1 - x), h: clamp(box.sh / h, 0, 1 - y) }
}

/** The crop that shows `r` in frame `f` — reopening the dialog on the crop
 *  you chose last time rather than starting again. */
export function cropFromNormIn(f: Frame, w: number, h: number, r: NormRect): Crop {
  const s = f.w / Math.max(1e-6, r.w * w)
  return clampCropIn(f, w, h, {
    zoom: s / coverScale(f, w, h),
    cx: (r.x + r.w / 2) * w,
    cy: (r.y + r.h / 2) * h,
  })
}

/** Nothing cut off (to a pixel in a thousand): treat as no crop at all. */
export function isWhole(r: NormRect | null | undefined): boolean {
  return !r || (r.x < 0.001 && r.y < 0.001 && r.w > 0.999 && r.h > 0.999)
}

/** The pixels of `r` in a photo `w` x `h`, whole and inside it. */
export function pixelBox(r: NormRect, w: number, h: number): CropBox {
  const sx = Math.max(0, Math.floor(r.x * w))
  const sy = Math.max(0, Math.floor(r.y * h))
  const sw = Math.max(1, Math.min(w - sx, Math.round(r.w * w)))
  const sh = Math.max(1, Math.min(h - sy, Math.round(r.h * h)))
  return { sx, sy, sw, sh }
}

/* ── THE SHAPES A FEED PHOTO MAY TAKE ───────────────────────────────────────
 *
 * Fixed shapes, never free corners: dragging four corners is fiddly on a
 * phone, and a shape plus drag-and-zoom is the gesture the card photo already
 * taught. Square is what a two-or-more post shows anyway (hub.css
 * .photos--grid), so choosing it is choosing your own tile; 4:5 is the tall
 * shape that still fits a phone screen; 16:9 is a site panorama.
 */
export type CropShapeKey = 'original' | 'square' | 'portrait' | 'wide'
export const CROP_SHAPES: { key: CropShapeKey; aspect: number | null }[] = [
  { key: 'original', aspect: null },
  { key: 'square', aspect: 1 },
  { key: 'portrait', aspect: 4 / 5 },
  { key: 'wide', aspect: 16 / 9 },
]

/** The shape's width / height; Original is the photo's own. */
export function shapeAspect(key: CropShapeKey, w: number, h: number): number {
  return CROP_SHAPES.find((s) => s.key === key)?.aspect ?? w / h
}

/** The largest frame of `aspect` inside a `bound` px square. */
export function frameFor(aspect: number, bound: number): Frame {
  return aspect >= 1
    ? { w: bound, h: Math.max(1, Math.round(bound / aspect)) }
    : { w: Math.max(1, Math.round(bound * aspect)), h: bound }
}

/* ── TAGS FOLLOW THE CROP ────────────────────────────────────────────────────
 *
 * 161's spot is a fraction of the feed copy. Re-cropping changes what the
 * feed copy is, so each spot is carried from the old crop into the new one.
 * A spot that falls outside the new crop loses its place, not its person:
 * the tag stays and is simply not pinned (x/y are both-or-neither, 161).
 */
export function moveSpot(
  x: number | null,
  y: number | null,
  from: NormRect,
  to: NormRect,
): { x: number | null; y: number | null } {
  if (x === null || y === null) return { x: null, y: null }
  const nx = (from.x + x * from.w - to.x) / to.w
  const ny = (from.y + y * from.h - to.y) / to.h
  if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return { x: null, y: null }
  return { x: Math.round(nx * 10000) / 10000, y: Math.round(ny * 10000) / 10000 }
}
